import { useEffect, useRef } from 'react';
export const useHorizontalPullToLoad = _ref => {
  let onTrigger = _ref.onTrigger,
    enabled = _ref.enabled,
    _ref$triggerAt = _ref.triggerAt,
    triggerAt = _ref$triggerAt === void 0 ? 100 : _ref$triggerAt,
    _ref$settleDelay = _ref.settleDelay,
    settleDelay = _ref$settleDelay === void 0 ? 120 : _ref$settleDelay;
  const rootRef = useRef(null);
  const spacerRef = useRef(null);
  const onTriggerRef = useRef(onTrigger);
  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);
  useEffect(() => {
    const root = rootRef.current;
    if (!enabled || !root) {
      return;
    }

    // Scroll position where the last tile's right edge sits flush with the
    // container's right edge — no pull-space is visible. Measured, not assumed,
    // so it stays consistent with the `scrollWidth` it is subtracted from; a
    // detached spacer means no pull-space and therefore no overshoot.
    const restingScrollLeft = () => {
      var _spacerRef$current;
      return Math.max(0, root.scrollWidth - root.clientWidth - (((_spacerRef$current = spacerRef.current) == null ? void 0 : _spacerRef$current.offsetWidth) ?? 0));
    };

    // A non-null `settleTimer` doubles as "waiting for the post-release scroll
    // to settle" — set on touchend, re-armed by scroll events while momentum
    // is still moving, and nulled inside `decide` so its own smooth scrollTo
    // doesn't re-arm and re-decide.
    let settleTimer = null;
    const clearSettle = () => {
      if (settleTimer !== null) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }
    };
    const decide = () => {
      settleTimer = null;
      const target = restingScrollLeft();
      const overshoot = root.scrollLeft - target;
      if (overshoot <= 0) {
        return;
      }
      if (overshoot >= triggerAt) {
        // Leave scroll where it is so the pull-space stays visible and the
        // incoming skeletons/tiles render into the user's viewport.
        onTriggerRef.current();
        return;
      }
      root.scrollTo({
        left: target,
        behavior: 'smooth'
      });
    };
    const scheduleSettle = () => {
      clearSettle();
      settleTimer = setTimeout(decide, settleDelay);
    };
    const onScroll = () => {
      if (settleTimer !== null) {
        scheduleSettle();
      }
    };
    root.addEventListener('touchstart', clearSettle, {
      passive: true
    });
    root.addEventListener('touchend', scheduleSettle, {
      passive: true
    });
    root.addEventListener('touchcancel', scheduleSettle, {
      passive: true
    });
    root.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      clearSettle();
      root.removeEventListener('touchstart', clearSettle);
      root.removeEventListener('touchend', scheduleSettle);
      root.removeEventListener('touchcancel', scheduleSettle);
      root.removeEventListener('scroll', onScroll);
    };
  }, [enabled, triggerAt, settleDelay]);
  return {
    rootRef,
    spacerRef
  };
};
//# sourceMappingURL=useHorizontalPullToLoad.js.map